import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'

const G = '#d4a843'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'
const EM = '#4ade80'

interface Assignment {
    id: string
    title: string
    prompt: string
    word_min: number
    word_max: number
    rubric: { criterion: string; points: number; description: string }[]
    max_score: number
    personal_context_hint: string | null
}

interface Submission {
    id: string
    grading_status: string
    ai_score: number | null
    max_score: number | null
    ai_feedback: string | null
    score_breakdown: { criterion: string; awarded: number; max: number; comment: string }[] | null
    follow_up_question: string | null
}

export default function ShortAnswerBox({
    classId, moduleId, lessonId, onDismiss, onComplete
}: {
    classId: string
    moduleId: number
    lessonId: number
    onDismiss: () => void
    onComplete?: (m: number, l: number) => void
}) {
    const { t } = useTranslation()
    const { user } = useAuth()

    const [assignment, setAssignment] = useState<Assignment | null>(null)
    const [checked, setChecked] = useState(false)
    const [open, setOpen] = useState(false)
    const [text, setText] = useState('')
    const [pasteDetected, setPaste] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submission, setSubmission] = useState<Submission | null>(null)
    const [previousSubmission, setPrev] = useState<Submission | null>(null)
    const [polling, setPolling] = useState(false)
    const typingStart = useRef<number | null>(null)
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // Check for an active assignment for this lesson
    useEffect(() => {
        if (!classId) return
        supabase
            .from('university_assignments')
            .select('id, title, prompt, word_min, word_max, rubric, max_score, personal_context_hint')
            .eq('class_id', classId)
            .eq('module_id', moduleId)
            .eq('lesson_id', lessonId)
            .eq('assignment_type', 'short_answer')
            .eq('is_active', true)
            .maybeSingle()
            .then(async ({ data }) => {
                setAssignment(data as Assignment | null)
                setChecked(true)
                if (data) {
                    setOpen(true) // auto-open when assignment exists
                    if (user) {
                        const { data: prevSub } = await supabase
                            .from('university_submissions')
                            .select('id, grading_status, ai_score, max_score, ai_feedback, score_breakdown, follow_up_question')
                            .eq('user_id', user.id)
                            .eq('assignment_id', data.id)
                            .in('grading_status', ['graded', 'flagged'])
                            .order('attempt_number', { ascending: false })
                            .limit(1)
                            .maybeSingle()
                        if (prevSub) setPrev(prevSub as Submission)
                    }
                }
            })
    }, [classId, moduleId, lessonId, user])

    // Poll for grading result
    useEffect(() => {
        if (!polling || !submission) return
        pollRef.current = setInterval(async () => {
            const { data } = await supabase
                .from('university_submissions')
                .select('id, grading_status, ai_score, max_score, ai_feedback, score_breakdown, follow_up_question')
                .eq('id', submission.id)
                .single()
            if (data && data.grading_status !== 'pending' && data.grading_status !== 'grading') {
                setSubmission(data as Submission)
                setPolling(false)
                // Mark complete in DB immediately once graded
                if (onComplete) onComplete(moduleId, lessonId + 1)
            }
        }, 2500)
        return () => { if (pollRef.current) clearInterval(pollRef.current) }
    }, [polling, submission])

    if (!checked || !assignment) return null

    const words = text.trim().split(/\s+/).filter(Boolean).length
    const tooShort = words < assignment.word_min
    const tooLong = words > assignment.word_max
    const canSubmit = !tooShort && !tooLong && !submitting && !submission
    const pct = Math.min((words / assignment.word_min) * 100, 100)

    function handlePaste() {
        if (words < 5) return // pasting in an almost-empty box is fine
        setPaste(true)
    }

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        if (!typingStart.current) typingStart.current = Date.now()
        setText(e.target.value)
    }

    async function handleSubmit() {
        if (!canSubmit || !user || !assignment) return
        setSubmitting(true)

        const typingDuration = typingStart.current
            ? Math.round((Date.now() - typingStart.current) / 1000)
            : null

        // Get existing attempts count
        const { count } = await supabase
            .from('university_submissions')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('assignment_id', assignment.id)

        const attemptNumber = (count || 0) + 1

        const { data: sub, error } = await supabase
            .from('university_submissions')
            .insert({
                user_id: user.id,
                assignment_id: assignment.id,
                class_id: classId,
                module_id: moduleId,
                lesson_id: lessonId,
                response_text: text.trim(),
                word_count: words,
                paste_detected: pasteDetected,
                typing_duration_seconds: typingDuration,
                attempt_number: attemptNumber,
                grading_status: 'pending',
            })
            .select('id, grading_status, ai_score, max_score, ai_feedback, score_breakdown, follow_up_question')
            .single()

        if (error || !sub) { setSubmitting(false); return }

        setSubmission(sub as Submission)
        setPolling(true)
        setSubmitting(false)

        // Trigger grading edge function (fire-and-forget)
        supabase.functions.invoke('grade-essay', {
            body: { submission_id: sub.id },
        }).catch(console.error)
    }

    const graded = submission?.grading_status === 'graded' || submission?.grading_status === 'flagged'

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    style={{
                        background: 'rgba(212,168,67,0.05)',
                        border: '1.5px solid rgba(212,168,67,0.3)',
                        borderRadius: 20,
                        padding: '24px',
                        marginBottom: 20,
                    }}
                >
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                        <div>
                            <p style={{ fontSize: 11, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 4 }}>
                                ✍️ {t('university.assessment.shortAnswer', 'SHORT ANSWER ASSESSMENT')}
                            </p>
                            <p style={{ fontWeight: 700, fontSize: 15, color: TX }}>{assignment.title}</p>
                        </div>
                        {!graded && (
                            <button onClick={onDismiss}
                                style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontSize: 18, padding: 0 }}>
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Previous result banner */}
                    {previousSubmission && !submission && (
                        <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 12, padding: '10px 14px', marginBottom: 16 }}>
                            <p style={{ fontSize: 12, color: EM, margin: 0, fontWeight: 600 }}>
                                ✓ You've already completed this. Score: {previousSubmission.ai_score}/{previousSubmission.max_score} — you can resubmit to improve.
                            </p>
                        </div>
                    )}

                    {/* Prompt */}
                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                        <p style={{ fontSize: 14, color: TX, lineHeight: 1.65, margin: 0 }}>{assignment.prompt}</p>
                        {assignment.personal_context_hint && (
                            <p style={{ fontSize: 12, color: G, margin: '10px 0 0', fontStyle: 'italic' }}>
                                💡 {assignment.personal_context_hint}
                            </p>
                        )}
                    </div>

                    {/* Only show textarea if not yet submitted */}
                    {!submission && (
                        <>
                            <textarea
                                value={text}
                                onChange={handleChange}
                                onPaste={handlePaste}
                                placeholder={t('university.assessment.placeholder', `Write your response here (${assignment.word_min}–${assignment.word_max} words)…`)}
                                rows={6}
                                style={{
                                    width: '100%',
                                    background: 'rgba(255,255,255,0.03)',
                                    border: `1.5px solid ${tooLong ? '#f87171' : BR}`,
                                    borderRadius: 12,
                                    padding: '14px 16px',
                                    color: TX,
                                    fontSize: 14,
                                    lineHeight: 1.6,
                                    resize: 'vertical',
                                    outline: 'none',
                                    fontFamily: "'Inter', sans-serif",
                                    boxSizing: 'border-box',
                                    transition: 'border-color 0.2s',
                                }}
                            />
                            {/* Word count bar */}
                            <div style={{ marginTop: 10, marginBottom: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span style={{ fontSize: 12, color: tooLong ? '#f87171' : tooShort ? MU : EM }}>
                                        {words} {t('university.assessment.words', 'words')}
                                    </span>
                                    <span style={{ fontSize: 12, color: MU }}>
                                        {t('university.assessment.wordRange', 'Required: {{min}}–{{max}}', { min: assignment.word_min, max: assignment.word_max })}
                                    </span>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 100, height: 4, overflow: 'hidden' }}>
                                    <motion.div
                                        animate={{ width: `${pct}%` }}
                                        style={{ height: '100%', borderRadius: 100, background: tooLong ? '#f87171' : tooShort ? G : EM }}
                                    />
                                </div>
                            </div>

                            {pasteDetected && (
                                <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                                    <p style={{ fontSize: 12, color: '#fbbf24', margin: 0 }}>
                                        📋 {t('university.assessment.pasteWarning', 'Paste detected. Your professor may ask a follow-up question to verify understanding.')}
                                    </p>
                                </div>
                            )}

                            <motion.button
                                whileHover={{ scale: canSubmit ? 1.02 : 1 }}
                                whileTap={{ scale: canSubmit ? 0.97 : 1 }}
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                style={{
                                    width: '100%',
                                    background: canSubmit ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.06)',
                                    color: canSubmit ? '#06060c' : MU,
                                    border: 'none',
                                    borderRadius: 12,
                                    padding: '13px',
                                    fontSize: 14,
                                    fontWeight: 800,
                                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {submitting
                                    ? t('university.assessment.submitting', 'Submitting…')
                                    : tooShort
                                        ? t('university.assessment.tooShort', `Minimum ${assignment.word_min} words`)
                                        : tooLong
                                            ? t('university.assessment.tooLong', `Maximum ${assignment.word_max} words`)
                                            : t('university.assessment.submit', 'Submit for Grading →')}
                            </motion.button>
                        </>
                    )}

                    {/* Submitted — waiting for grade */}
                    {submission && !graded && (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                                style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(212,168,67,0.15)', borderTopColor: G, margin: '0 auto 16px' }}
                            />
                            <p style={{ color: MU, fontSize: 14 }}>
                                {t('university.assessment.grading', 'Your professor is reviewing your response…')}
                            </p>
                        </div>
                    )}

                    {/* Graded result */}
                    {graded && submission && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            {/* Score */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '16px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 14 }}>
                                <div>
                                    <p style={{ fontSize: 12, color: EM, fontWeight: 700, marginBottom: 4 }}>
                                        {t('university.assessment.yourScore', 'YOUR SCORE')}
                                    </p>
                                    <p style={{ fontSize: 28, fontWeight: 900, color: EM, margin: 0 }}>
                                        {submission.ai_score}/{submission.max_score}
                                    </p>
                                </div>
                                <div style={{ fontSize: 40 }}>
                                    {(submission.ai_score || 0) / (submission.max_score || 1) >= 0.8 ? '🎓' : (submission.ai_score || 0) / (submission.max_score || 1) >= 0.6 ? '✅' : '📚'}
                                </div>
                            </div>

                            {/* Breakdown */}
                            {submission.score_breakdown && submission.score_breakdown.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    {submission.score_breakdown.map((item, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${BR}` }}>
                                            <div style={{ flex: 1, paddingRight: 12 }}>
                                                <p style={{ fontSize: 13, fontWeight: 700, color: TX, margin: '0 0 2px' }}>{item.criterion}</p>
                                                <p style={{ fontSize: 12, color: MU, margin: 0, lineHeight: 1.5 }}>{item.comment}</p>
                                            </div>
                                            <span style={{ fontSize: 13, fontWeight: 800, color: G, flexShrink: 0 }}>{item.awarded}/{item.max}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* AI Feedback */}
                            {submission.ai_feedback && (
                                <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 12, padding: '16px', marginBottom: 16 }}>
                                    <p style={{ fontSize: 11, fontWeight: 800, color: G, letterSpacing: '1px', marginBottom: 10 }}>
                                        🎓 {t('university.assessment.professorFeedback', "PROFESSOR'S FEEDBACK")}
                                    </p>
                                    <p style={{ fontSize: 13, color: TX, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {submission.ai_feedback}
                                    </p>
                                </div>
                            )}

                            {/* Follow-up question if paste detected */}
                            {submission.follow_up_question && (
                                <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                                    <p style={{ fontSize: 12, fontWeight: 800, color: '#fbbf24', marginBottom: 8 }}>
                                        📋 {t('university.assessment.followUpTitle', 'VERIFICATION QUESTION')}
                                    </p>
                                    <p style={{ fontSize: 13, color: TX, lineHeight: 1.6, margin: 0 }}>
                                        {submission.follow_up_question}
                                    </p>
                                    <p style={{ fontSize: 11, color: MU, margin: '10px 0 0' }}>
                                        {t('university.assessment.followUpHint', 'Answer this in the chat above to confirm your understanding.')}
                                    </p>
                                </div>
                            )}

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                onClick={onDismiss}
                                style={{
                                    width: '100%',
                                    background: `linear-gradient(135deg, ${G}, #b8912e)`,
                                    color: '#06060c',
                                    border: 'none',
                                    borderRadius: 12,
                                    padding: '13px',
                                    fontSize: 14,
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                }}
                            >
                                {t('university.assessment.nextLesson', 'Continue to Next Lesson →')}
                            </motion.button>
                        </motion.div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}
